import { type CylinderSurface, cylinderSurfaceSchema, type SizeMm } from '@repo/api-contracts';
import { curvePxOf, curveRatioOf, type PhotoDimensions, rectPoints } from '@/entities/edit-area';
import { koreanMessageOr } from '@/shared/lib';

/** 원통 인스펙터 입력 칸 — 보이는 범위(사진 %) 넷과 곡률(기준 사진 px) 둘. */
export type CylinderInputField = 'left' | 'top' | 'right' | 'bottom' | 'topCurve' | 'bottomCurve';
/** 운영자가 고친 칸의 글자만 담는다. 없는 칸은 저장된 값을 그대로 쓴다. */
export type CylinderInputEdits = Readonly<Partial<Record<CylinderInputField, string>>>;
type CylinderInputResult =
  | { readonly surface: CylinderSurface; readonly error?: undefined }
  | { readonly surface?: undefined; readonly error: string };

interface CylinderInput {
  readonly id: string;
  /** 고치는 저장 표면. 없으면 새 표면이다. */
  readonly surface: CylinderSurface | undefined;
  /** 저장할 실측 — 저장 표면의 실측, 새 표면이면 상품 실측의 원통 프레임. 상품 실측이 없으면 null. */
  readonly realSize: SizeMm | null;
  readonly edits: CylinderInputEdits;
  /** 기준 사진 색상의 이 면 사진 크기. 없으면 곡률 칸을 고칠 수 없어 저장된 비율을 쓴다. */
  readonly photo: PhotoDimensions | undefined;
}

const RANGE_KEYS = ['left', 'top', 'right', 'bottom'] as const;
/** 곡률 px 표시 자릿수. 표시 전용이다 — 고치지 않은 곡률 칸은 이 글자가 아니라 저장된 비율을 쓴다. */
const CURVE_PX_FRACTION_DIGITS = 2;

const MEASUREMENT_REQUIRED = '상품 실측을 먼저 등록해 주세요.';
const RANGE_ERROR = '보이는 범위는 사진 안(0–100%)의 직사각형이어야 합니다.';
const CURVE_ERROR = '곡률은 사진 px 숫자로 입력해 주세요.';
/** 곡률 px 를 고쳐 둔 채 기준 사진이 사라지면 비율로 바꿀 분모가 없다 — 저장된 비율로 대신하지 않고 거부한다(결정 D-Q). */
const CURVE_PHOTO_REQUIRED =
  '기준 사진이 없어 곡률을 적용할 수 없습니다. 사진이 있는 색상을 고르거나 곡률 입력을 되돌려 주세요.';
const SURFACE_ERROR = '표면 값을 확인해 주세요.';

/** 계약 이슈 자리의 한국어 대체 문구. 계약이 붙인 한국어 문구는 그대로 보이고, zod 기본 영어 문구만 바꾼다. */
const fallbackOf = (path: readonly PropertyKey[]): string => {
  if (path[0] === 'points') return RANGE_ERROR;
  if (path[0] === 'topCurveRatio' || path[0] === 'bottomCurveRatio') return CURVE_ERROR;
  return SURFACE_ERROR;
};

const storedRange = (surface: CylinderSurface): Record<(typeof RANGE_KEYS)[number], number> => ({
  left: surface.points[0].x,
  top: surface.points[0].y,
  right: surface.points[2].x,
  bottom: surface.points[2].y,
});

const curvePxText = (
  ratio: number,
  points: CylinderSurface['points'],
  photo: PhotoDimensions,
): string => String(Number(curvePxOf(ratio, points, photo).toFixed(CURVE_PX_FRACTION_DIGITS)));

/**
 * 입력 칸에 처음 보일 글자. 보이는 범위는 저장된 % 그대로이고, 곡률은 기준 사진 px 로 반올림해 보인다(결정 Q1).
 * 새 표면은 보이는 범위가 비어 있고 곡률은 0 이다. 기준 사진이 없으면 저장 표면의 곡률 px 를 알 수 없어 비운다.
 */
export const shownCylinderInput = (
  surface: CylinderSurface | undefined,
  photo: PhotoDimensions | undefined,
): Record<CylinderInputField, string> => {
  if (surface === undefined)
    return { left: '', top: '', right: '', bottom: '', topCurve: '0', bottomCurve: '0' };
  const range = storedRange(surface);
  return {
    left: String(range.left),
    top: String(range.top),
    right: String(range.right),
    bottom: String(range.bottom),
    topCurve: photo === undefined ? '' : curvePxText(surface.topCurveRatio, surface.points, photo),
    bottomCurve:
      photo === undefined ? '' : curvePxText(surface.bottomCurveRatio, surface.points, photo),
  };
};

/** 고친 칸 글자의 숫자. 비었거나 유한수가 아니면 null. */
const editedNumber = (text: string): number | null => {
  const value = Number(text);
  return text.trim() === '' || !Number.isFinite(value) ? null : value;
};

/**
 * 저장할 보이는 사각형. 범위 칸을 하나도 고치지 않았으면 저장된 points 를 그대로 둔다.
 * 고친 칸만 숫자로 바꾸고, 나머지 칸은 저장된 double 을 쓴다. 비었거나 숫자가 아닌 칸이 있으면 null.
 */
const pointsOf = (
  surface: CylinderSurface | undefined,
  edits: CylinderInputEdits,
): CylinderSurface['points'] | null => {
  if (surface !== undefined && RANGE_KEYS.every((key) => edits[key] === undefined))
    return surface.points;
  const stored = surface === undefined ? undefined : storedRange(surface);
  const rangeValue = (key: (typeof RANGE_KEYS)[number]): number | null => {
    const text = edits[key];
    return text === undefined ? (stored?.[key] ?? null) : editedNumber(text);
  };
  const left = rangeValue('left');
  const top = rangeValue('top');
  const right = rangeValue('right');
  const bottom = rangeValue('bottom');
  if (left === null || top === null || right === null || bottom === null) return null;
  return rectPoints(left, top, right, bottom);
};

/**
 * 저장할 곡률 비율. 곡률 칸을 고쳤을 때만 px 를 새 보이는 사각형 높이로 나눠 다시 계산한다(결정 Q1) —
 * 사각형만 바뀌면 저장된 비율을 그대로 둔다. 새 표면의 고치지 않은 곡률은 0 이다.
 * 고친 곡률이 있는데 기준 사진이 없으면(입력 중 사진 없는 색상으로 바뀜) 입력을 삼키지 않고 거부한다(결정 D-Q).
 */
const curveRatioFor = (
  text: string | undefined,
  stored: number,
  points: CylinderSurface['points'],
  photo: PhotoDimensions | undefined,
): number | string => {
  if (text === undefined) return stored;
  if (photo === undefined) return CURVE_PHOTO_REQUIRED;
  const px = editedNumber(text);
  if (px === null) return CURVE_ERROR;
  return curveRatioOf(px, points, photo) ?? RANGE_ERROR;
};

const outlineOf = (surface: CylinderSurface | undefined): Pick<CylinderSurface, 'outline'> =>
  surface?.outline === undefined ? {} : { outline: surface.outline };

/**
 * 원통 인스펙터 입력으로 저장할 원통 표면을 만든다. 검사는 계약 cylinderSurfaceSchema 로 하고, 계약의 한국어 문구를
 * 그대로 돌려준다. 윤곽은 실루엣 참고용이라 보이는 사각형을 바꿔도 그대로 둔다(ADR-0007 D7).
 */
export const cylinderSurfaceFromInput = ({
  id,
  surface,
  realSize,
  edits,
  photo,
}: CylinderInput): CylinderInputResult => {
  if (realSize === null) return { error: MEASUREMENT_REQUIRED };
  const points = pointsOf(surface, edits);
  if (points === null) return { error: RANGE_ERROR };
  const topCurveRatio = curveRatioFor(edits.topCurve, surface?.topCurveRatio ?? 0, points, photo);
  if (typeof topCurveRatio === 'string') return { error: topCurveRatio };
  const bottomCurveRatio = curveRatioFor(
    edits.bottomCurve,
    surface?.bottomCurveRatio ?? 0,
    points,
    photo,
  );
  if (typeof bottomCurveRatio === 'string') return { error: bottomCurveRatio };
  const parsed = cylinderSurfaceSchema.safeParse({
    id,
    type: 'cylinder',
    realSize,
    points,
    topCurveRatio,
    bottomCurveRatio,
    ...outlineOf(surface),
  });
  if (parsed.success) return { surface: parsed.data };
  const issue = parsed.error.issues[0];
  return { error: koreanMessageOr(issue?.message, fallbackOf(issue?.path ?? [])) };
};
